import type en from '../en/templates'
import type { Translation } from '../types'

/** Configuration templates: the list, the wizard and the blueprints it renders */
const templates: Translation<typeof en> = {
  "templates.title": "Sjablonen",
  "templates.subtitle": "Herbruikbare configuratie voor uw locaties",
  "templates.new": "Nieuw sjabloon",
  "templates.allTypes": "Alle typen",
  "templates.empty": "Nog geen sjablonen",
  "templates.emptyText": "Maak een sjabloon door de configuratie van een bestaande locatie over te nemen, of stel er zelf een samen.",
  "templates.createFirst": "Eerste sjabloon maken",
  "templates.family.ANY": "Elk apparaat",
  "templates.defaultBadge": "STANDAARD",
  "templates.origin.PULLED": "overgenomen",
  "templates.origin.MANUAL": "samengesteld",
  "templates.system": "Systeem",
  "templates.applications": { one: "{count} keer toegepast", other: "{count} keer toegepast" },
  "templates.editHint": "Het sjabloon en de configuratie bewerken",
  "templates.apply": "Toepassen",
  "templates.deleteNamed": "{name} verwijderen",
  "templates.deleteConfirm": "Sjabloon \"{name}\" verwijderen?",
  "templates.applyTitle": "Sjabloon toepassen",
  "templates.queued": { one: "In de wachtrij voor {count} locatie", other: "In de wachtrij voor {count} locaties" },
  "templates.queuedText": "Elke locatie meldt welke velden zijn toegepast. Niet-ondersteunde velden worden vastgelegd, niet stilletjes weggelaten.",
  "templates.done": "Klaar",
  "templates.targets": "\"{name}\" is bedoeld voor {family}-apparaten.",
  "templates.noCompatible": "Geen enkele verbonden locatie kan dit sjabloon aannemen. Controleer voor welke apparaatfamilie het is.",
  "templates.partial": { one: "{count} gekozen locatie slaat enkele velden over:", other: "{count} gekozen locaties slaan enkele velden over:" },
  "templates.more": "+{count} meer",
  "templates.showIncompatible": { one: "{count} locatie tonen die het niet kan aannemen", other: "{count} locaties tonen die het niet kunnen aannemen" },
  "templates.hideIncompatible": { one: "{count} locatie verbergen die het niet kan aannemen", other: "{count} locaties verbergen die het niet kunnen aannemen" },
  "templates.notLicensed": "niet in uw licentie",
  "templates.noSupported": "geen ondersteunde velden",
  "templates.wrongFamily": "andere apparaatfamilie",
  "templates.applyTo": { one: "Toepassen op {count} locatie", other: "Toepassen op {count} locaties" },
  "templates.allSupported": "Alle velden ondersteund",
  "templates.skipped": { one: "{count} veld wordt overgeslagen", other: "{count} velden worden overgeslagen" },
  "templates.family.UNIFI": "Controllers en Site Manager",
  "templates.family.VELANOX": "One Speed gebundelde WAN",
  "templates.family.TELTONIKA": "RutOS mobiele routers",
  "templates.family.PEPLINK": "Balance / MAX met SpeedFusion",
  "templates.family.MIKROTIK": "RouterOS-routers en PtP- / PtMP-straalverbindingen",
  "templates.saveFailed": "Het sjabloon kon niet worden opgeslagen",
  "templates.pullFailed": "De configuratie kon niet worden overgenomen",
  "templates.updated": "Sjabloon bijgewerkt",
  "templates.saved": "Sjabloon opgeslagen",
  "templates.viewAll": "Sjablonen bekijken",
  "templates.createAnother": "Nog een maken",
  "templates.back": "Terug",
  "templates.editing": "Bewerken: {name}",
  "templates.whichKind": "Voor welk soort apparaat is dit?",
  "templates.sitesConnected": { one: "{count} locatie verbonden", other: "{count} locaties verbonden" },
  "templates.named": "Sjabloon {name}",
  "templates.whatConfigure": "Wat wilt u configureren?",
  "templates.variant": "Variant",
  "templates.variantHint": "Varianten die via de cloud worden beheerd, ondersteunen minder wijzigingen dan directe toegang tot het apparaat.",
  "templates.loadingTypes": "Sjabloontypen laden…",
  "templates.howCreate": "Hoe wilt u het maken?",
  "templates.buildWizard": "Samenstellen met de wizard",
  "templates.buildHint": "Stap voor stap, met alleen wat {family}-apparaten ondersteunen.",
  "templates.pullFromSite": "Overnemen van een locatie",
  "templates.noPullSites": "Nog geen locaties met een UniFi-controller verbonden.",
  "templates.pullOnlyFor": "Beschikbaar voor netwerk-, wifi- en WAN-sjablonen van een locatie met een UniFi-controller.",
  "templates.pullFrom": { one: "Kopieer de actuele configuratie van uw locatie met een UniFi-controller.", other: "Kopieer de actuele configuratie van een van uw {count} locaties met een UniFi-controller." },
  "templates.sourceSite": "Bronlocatie *",
  "templates.chooseSite": "Kies een locatie…",
  "templates.name": "Naam van het sjabloon *",
  "templates.description": "Beschrijving",
  "templates.optional": "Optioneel",
  "templates.optionalParen": "(optioneel)",
  "templates.pullConfig": "Configuratie overnemen",
  "templates.coverage": "{provider} ondersteunt {count} van de {total} opties voor dit sjabloontype.",
  "templates.hiddenSteps": { one: "{count} stap is helemaal verborgen.", other: "{count} stappen zijn helemaal verborgen." },
  "templates.stepOf": "Stap {n} van {total}",
  "templates.nothingHere": "Niets te configureren in deze stap voor {provider}.",
  "templates.showUnsupported": { one: "{count} niet-ondersteunde optie tonen", other: "{count} niet-ondersteunde opties tonen" },
  "templates.hideUnsupported": { one: "{count} niet-ondersteunde optie verbergen", other: "{count} niet-ondersteunde opties verbergen" },
  "templates.saveTemplate": "Sjabloon opslaan",
  "templates.updateTemplate": "Sjabloon bijwerken",
  "templates.myName": "Mijn {name}",
  "templates.useSuggestion": "\"{name}\" gebruiken",
  "templates.next": "Volgende",
  "templates.notSupported": "niet ondersteund",
  "templates.enabled": "Ingeschakeld",
  "templates.disabled": "Uitgeschakeld",
  "templates.choose": "Kies…",
  "templates.typeEnter": "Typ en druk op Enter",
  "templates.add": "Toevoegen",
  "templates.removeNamed": "{name} verwijderen",
  "templates.inUse": "in gebruik: {list}",
  "templates.type.NETWORK": "Netwerk / VLAN",
  "templates.typeDesc.NETWORK": "VLAN-indeling, DHCP-bereik en isolatie",
  "templates.step.NETWORK.identity": "Doel en identiteit",
  "templates.stepDesc.NETWORK.identity": "Waar dit netwerk voor is",
  "templates.field.NETWORK.purpose": "Doel",
  "templates.option.NETWORK.purpose.production": "🏭 Productie",
  "templates.option.NETWORK.purpose.cash-registers": "🏪 Kassa’s",
  "templates.option.NETWORK.purpose.entrance": "🚪 Entree / lobby",
  "templates.option.NETWORK.purpose.cp": "🖥️ CP / bedieningspaneel",
  "templates.option.NETWORK.purpose.employers": "👔 Medewerkers",
  "templates.option.NETWORK.purpose.media": "📺 Media / AV",
  "templates.option.NETWORK.purpose.iot": "📡 IoT-apparaten",
  "templates.option.NETWORK.purpose.guest": "🌐 Gasten / bezoekers",
  "templates.option.NETWORK.purpose.vlan-only": "🔒 Alleen VLAN (geen DHCP)",
  "templates.field.NETWORK.name": "Netwerknaam",
  "templates.field.NETWORK.vlanId": "VLAN ID",
  "templates.help.NETWORK.vlanId": "Voorgesteld op basis van VLAN’s die uw sjablonen al gebruiken",
  "templates.step.NETWORK.addressing": "IP-adressen en DHCP",
  "templates.stepDesc.NETWORK.addressing": "Subnet en adresbereik",
  "templates.field.NETWORK.subnet": "Subnet",
  "templates.help.NETWORK.subnet": "bijv. 192.168.30.0/24",
  "templates.field.NETWORK.dhcp.enabled": "DHCP-server",
  "templates.field.NETWORK.dhcp.start": "DHCP-begin",
  "templates.field.NETWORK.dhcp.end": "DHCP-einde",
  "templates.field.NETWORK.dhcp.dns": "DNS-servers",
  "templates.step.NETWORK.security": "Beveiliging",
  "templates.stepDesc.NETWORK.security": "Isolatie en bescherming",
  "templates.field.NETWORK.isolation": "Clientisolatie",
  "templates.help.NETWORK.isolation": "Voorkomt dat apparaten in dit netwerk met elkaar praten",
  "templates.field.NETWORK.dhcpGuard": "DHCP-bewaking",
  "templates.help.NETWORK.dhcpGuard": "Blokkeert ongewenste DHCP-servers",
  "templates.field.NETWORK.igmpSnooping": "IGMP snooping",
  "templates.type.WIFI": "Wifinetwerk",
  "templates.typeDesc.WIFI": "SSID, beveiliging en radiogedrag",
  "templates.step.WIFI.identity": "SSID en identiteit",
  "templates.stepDesc.WIFI.identity": "Naam en zichtbaarheid",
  "templates.field.WIFI.ssid": "SSID",
  "templates.field.WIFI.enabled": "Netwerk actief",
  "templates.field.WIFI.isHidden": "Verborgen SSID",
  "templates.field.WIFI.isGuest": "Gastnetwerk",
  "templates.help.WIFI.isGuest": "Isoleert clients van het LAN",
  "templates.step.WIFI.security": "Beveiliging",
  "templates.stepDesc.WIFI.security": "Versleuteling en inloggegevens",
  "templates.field.WIFI.security": "Type beveiliging",
  "templates.option.WIFI.security.wpa3": "WPA3-Personal (aanbevolen)",
  "templates.option.WIFI.security.wpa2": "WPA2-Personal (compatibel)",
  "templates.option.WIFI.security.wpa2-enterprise": "WPA2-Enterprise (RADIUS)",
  "templates.option.WIFI.security.open": "Open — geen wachtwoord",
  "templates.field.WIFI.passphrase": "Wachtwoord",
  "templates.help.WIFI.passphrase": "Minimaal 8 tekens",
  "templates.field.WIFI.vlanId": "VLAN-tag",
  "templates.help.WIFI.vlanId": "Leeg laat de SSID zonder tag",
  "templates.step.WIFI.radio": "Radio en prestaties",
  "templates.stepDesc.WIFI.radio": "Banden en roaming",
  "templates.field.WIFI.band": "Band",
  "templates.option.WIFI.band.both": "2.4 + 5 GHz",
  "templates.option.WIFI.band.5g": "Alleen 5 GHz",
  "templates.option.WIFI.band.2g": "Alleen 2.4 GHz",
  "templates.option.WIFI.band.6g": "6 GHz (wifi 6E)",
  "templates.field.WIFI.txPower": "Zendvermogen",
  "templates.option.WIFI.txPower.auto": "Automatisch",
  "templates.option.WIFI.txPower.low": "Laag",
  "templates.option.WIFI.txPower.medium": "Gemiddeld",
  "templates.option.WIFI.txPower.high": "Hoog",
  "templates.field.WIFI.fastRoaming": "Snelle roaming (802.11r)",
  "templates.field.WIFI.minRssi": "Minimaal signaal",
  "templates.field.WIFI.scheduleEnabled": "Schema actief",
  "templates.type.WAN": "WAN / internet",
  "templates.typeDesc.WAN": "Gedrag van de uplinks, failover en load balancing",
  "templates.step.WAN.links": "Gedrag van de uplinks",
  "templates.stepDesc.WAN.links": "Hoe de WAN-verbindingen worden gebruikt",
  "templates.field.WAN.failoverEnabled": "Failover",
  "templates.help.WAN.failoverEnabled": "Overschakelen naar een reserveverbinding wanneer de primaire uitvalt",
  "templates.field.WAN.loadBalancingEnabled": "Load balancing",
  "templates.help.WAN.loadBalancingEnabled": "Verdeelt sessies over de actieve verbindingen",
  "templates.field.WAN.wanPortCount": "Aantal WAN-verbindingen",
  "templates.option.WAN.wanPortCount.2": "2",
  "templates.option.WAN.wanPortCount.3": "3",
  "templates.option.WAN.wanPortCount.4": "4",
  "templates.field.WAN.healthCheckEnabled": "Gezondheidscontrole",
  "templates.help.WAN.healthCheckEnabled": "Test elke verbinding en markeer haar als uitgevallen bij een storing",
  "templates.field.WAN.priorityMode": "Prioriteitsmodus",
  "templates.option.WAN.priorityMode.ordered": "Vaste volgorde",
  "templates.option.WAN.priorityMode.weighted": "Gewogen",
  "templates.step.WAN.quality": "Quality of service",
  "templates.stepDesc.WAN.quality": "Wachtrijbeheer en MTU",
  "templates.field.WAN.smartQueueEnabled": "Smart Queue (QoS)",
  "templates.help.WAN.smartQueueEnabled": "Vermindert bufferbloat onder belasting",
  "templates.field.WAN.smartQueueAutoDetect": "Snelheid automatisch bepalen bij uitrol",
  "templates.help.WAN.smartQueueAutoDetect": "Voert bij het toepassen een snelheidstest uit en gebruikt 90% van de uitkomst",
  "templates.field.WAN.smartQueueDown": "Downloadsnelheid",
  "templates.field.WAN.smartQueueUp": "Uploadsnelheid",
  "templates.field.WAN.mtu": "MTU",
  "templates.type.CELLULAR": "Mobiel / simkaart",
  "templates.typeDesc.CELLULAR": "APN, simbeleid, databundels en roaming",
  "templates.step.CELLULAR.carrier": "Provider en APN",
  "templates.stepDesc.CELLULAR.carrier": "Hoe de modem zich aanmeldt op het netwerk",
  "templates.field.CELLULAR.apn": "APN",
  "templates.help.CELLULAR.apn": "Leeg laten voor automatische herkenning door de provider",
  "templates.field.CELLULAR.apnUser": "APN-gebruikersnaam",
  "templates.field.CELLULAR.apnPassword": "APN-wachtwoord",
  "templates.field.CELLULAR.pin": "Pincode simkaart",
  "templates.step.CELLULAR.sim": "Simbeleid",
  "templates.stepDesc.CELLULAR.sim": "Welke simkaart wordt gebruikt en wanneer er wordt gewisseld",
  "templates.field.CELLULAR.simSwitch": "Voorkeur simslot",
  "templates.option.CELLULAR.simSwitch.1": "Simkaart 1",
  "templates.option.CELLULAR.simSwitch.2": "Simkaart 2",
  "templates.field.CELLULAR.simFailover": "Automatisch wisselen bij storing",
  "templates.help.CELLULAR.simFailover": "Valt terug op de andere simkaart als de verbinding wegvalt",
  "templates.field.CELLULAR.roaming": "Roaming toestaan",
  "templates.help.CELLULAR.roaming": "Kan duur zijn buiten het eigen netwerk",
  "templates.step.CELLULAR.limits": "Data en radio",
  "templates.stepDesc.CELLULAR.limits": "Limieten en bandkeuze",
  "templates.field.CELLULAR.dataLimit": "Maandelijkse datalimiet",
  "templates.help.CELLULAR.dataLimit": "Bij 90% hiervan volgt een melding",
  "templates.field.CELLULAR.dataLimitAction": "Als de limiet is bereikt",
  "templates.option.CELLULAR.dataLimitAction.alert": "Alleen melden",
  "templates.option.CELLULAR.dataLimitAction.throttle": "Snelheid beperken",
  "templates.option.CELLULAR.dataLimitAction.disconnect": "Verbinding verbreken",
  "templates.field.CELLULAR.bandLock": "Bandvergrendeling",
  "templates.help.CELLULAR.bandLock": "Bandnummers gescheiden door komma’s, leeg = automatisch",
  "templates.field.CELLULAR.networkMode": "Netwerkmodus",
  "templates.option.CELLULAR.networkMode.auto": "Automatisch",
  "templates.option.CELLULAR.networkMode.5g": "Voorkeur 5G",
  "templates.option.CELLULAR.networkMode.lte": "Alleen LTE",
  "templates.option.CELLULAR.networkMode.3g": "3G als terugval",
  "templates.type.TUNNEL": "Tunnel / bundeling",
  "templates.typeDesc.TUNNEL": "SpeedFusion-bundeling en VPN-clientprofielen",
  "templates.step.TUNNEL.profile": "Tunnelprofiel",
  "templates.stepDesc.TUNNEL.profile": "Tegenpartij en bundelmodus",
  "templates.field.TUNNEL.name": "Profielnaam",
  "templates.field.TUNNEL.peerHost": "Host van de tegenpartij",
  "templates.help.TUNNEL.peerHost": "Hostnaam of IP-adres van het eindpunt van de tunnel",
  "templates.field.TUNNEL.mode": "Modus",
  "templates.option.TUNNEL.mode.bonding": "Bundeling — bandbreedte combineren",
  "templates.option.TUNNEL.mode.failover": "Failover — warme reserve",
  "templates.option.TUNNEL.mode.overflow": "Overloop — naar de tweede verbinding",
  "templates.field.TUNNEL.encryption": "Versleuteling",
  "templates.option.TUNNEL.encryption.aes256": "AES-256",
  "templates.option.TUNNEL.encryption.aes128": "AES-128",
  "templates.option.TUNNEL.encryption.none": "Geen",
  "templates.step.TUNNEL.behaviour": "Gedrag",
  "templates.stepDesc.TUNNEL.behaviour": "Opnieuw verbinden en VPN-client",
  "templates.field.TUNNEL.autoReconnect": "Automatisch opnieuw verbinden",
  "templates.field.TUNNEL.vpnClientEnabled": "VPN-client",
  "templates.field.TUNNEL.vpnClientConfig": "VPN-profiel",
  "templates.type.SYSTEM": "Systeembasis",
  "templates.typeDesc.SYSTEM": "Tijd, identiteit en firmwarebeleid",
  "templates.step.SYSTEM.identity": "Identiteit en tijd",
  "templates.stepDesc.SYSTEM.identity": "Naamgeving en klok",
  "templates.field.SYSTEM.hostname": "Patroon voor de hostnaam",
  "templates.help.SYSTEM.hostname": "Gebruik {site} om de naam van de locatie in te voegen",
  "templates.field.SYSTEM.timezone": "Tijdzone",
  "templates.field.SYSTEM.ntp": "NTP-server",
  "templates.step.SYSTEM.maintenance": "Onderhoud",
  "templates.stepDesc.SYSTEM.maintenance": "Firmware en back-ups",
  "templates.field.SYSTEM.firmwareCheck": "Firmware controleren bij toepassen",
  "templates.field.SYSTEM.firmwareUpgrade": "Updates automatisch installeren",
  "templates.help.SYSTEM.firmwareUpgrade": "Installeert de nieuwste versie — veroorzaakt een herstart",
  "templates.field.SYSTEM.backup": "Back-up maken vóór wijzigingen",
  "templates.type.LINK": "Straalverbinding",
  "templates.typeDesc.LINK": "Instellingen voor punt-tot-punt- of punt-tot-multipuntradio — één sjabloon per kant",
  "templates.step.LINK.role": "Rol",
  "templates.stepDesc.LINK.role": "Welk uiteinde van de verbinding dit apparaat is",
  "templates.field.LINK.topology": "Topologie",
  "templates.option.LINK.topology.ptp": "Punt-tot-punt — één station",
  "templates.option.LINK.topology.ptmp": "Punt-tot-multipunt — meerdere stations",
  "templates.field.LINK.role": "Deze kant",
  "templates.help.LINK.role": "Pas eerst het stationsjabloon toe en daarna binnen 10 minuten het accesspoint — andersom verliezen de stations de verbinding. Een station houdt zijn nieuwe instellingen zodra het accesspoint overeenkomt; gebeurt dat niet op tijd, dan zet het zichzelf terug",
  "templates.option.LINK.role.ap": "Accesspoint (de sector / de nabije kant)",
  "templates.option.LINK.role.station": "Station (de CPE / de verre kant)",
  "templates.field.LINK.interface": "Radio-interface",
  "templates.help.LINK.interface": "Leeg kiest de linkradio als die er is (een station, een 60 GHz- of NV2/Nstreme-radio); een apparaat met meerdere radio’s heeft de naam nodig (wlan60-1, wlan1, wifi1 …)",
  "templates.step.LINK.radio": "Radio",
  "templates.stepDesc.LINK.radio": "Beide kanten moeten overeenkomen",
  "templates.field.LINK.ssid": "Linknaam (SSID)",
  "templates.field.LINK.linkKey": "Linksleutel",
  "templates.help.LINK.linkKey": "Minstens 8 tekens. NV2-sleutel, WPA2/WPA3-wachtwoord of 60 GHz-wachtwoord, afhankelijk van de radio",
  "templates.field.LINK.frequency": "Frequentie",
  "templates.help.LINK.frequency": "5 GHz bijv. 5500. 60 GHz: 58320, 60480, 62640, 64800 of 66000. Leeg houdt de huidige",
  "templates.field.LINK.channelWidth": "Kanaalbreedte",
  "templates.help.LINK.channelWidth": "Niet gebruikt op 60 GHz",
  "templates.option.LINK.channelWidth.20": "20 MHz",
  "templates.option.LINK.channelWidth.40": "40 MHz",
  "templates.option.LINK.channelWidth.80": "80 MHz",
  "templates.field.LINK.protocol": "Protocol",
  "templates.help.LINK.protocol": "Alleen de 2.4/5 GHz-driver \"wireless\"; wifi 6-radio’s gebruiken altijd 802.11",
  "templates.option.LINK.protocol.nv2": "NV2 (MikroTik TDMA — het beste voor buitenverbindingen)",
  "templates.option.LINK.protocol.802.11": "802.11 (standaard wifi)",
  "templates.option.LINK.protocol.nstreme": "Nstreme",
  "templates.option.LINK.protocol.any": "Elk (het accesspoint volgen)",
  "templates.field.LINK.txPower": "Zendvermogen",
  "templates.help.LINK.txPower": "Leeg houdt de huidige instelling. Blijf binnen de limiet van uw regelgevingsdomein",
  "templates.type.CYBERSECURE": "CyberSecure",
  "templates.typeDesc.CYBERSECURE": "IPS, inhoudsfiltering en dreigingsbeheer",
  "templates.step.CYBERSECURE.ips": "Inbraakpreventie",
  "templates.stepDesc.CYBERSECURE.ips": "Blokkeren op basis van signaturen",
  "templates.field.CYBERSECURE.ips.enabled": "IPS/IDS inschakelen",
  "templates.field.CYBERSECURE.ips.mode": "Modus",
  "templates.option.CYBERSECURE.ips.mode.ips": "IPS — dreigingen blokkeren",
  "templates.option.CYBERSECURE.ips.mode.ids": "IDS — alleen melden",
  "templates.step.CYBERSECURE.content": "Inhoudsfiltering",
  "templates.stepDesc.CYBERSECURE.content": "Blokkeren op categorie en domein",
  "templates.field.CYBERSECURE.contentFilter.enabled": "Inhoudsfiltering inschakelen",
  "templates.field.CYBERSECURE.contentFilter.categories": "Geblokkeerde categorieën",
  "templates.option.CYBERSECURE.contentFilter.categories.adult": "🔞 Volwassenen",
  "templates.option.CYBERSECURE.contentFilter.categories.gambling": "🎰 Gokken",
  "templates.option.CYBERSECURE.contentFilter.categories.malware": "🦠 Malware",
  "templates.option.CYBERSECURE.contentFilter.categories.p2p": "⬇️ P2P",
  "templates.option.CYBERSECURE.contentFilter.categories.ads": "📢 Advertenties",
  "templates.option.CYBERSECURE.contentFilter.categories.social": "💬 Sociale media",
  "templates.option.CYBERSECURE.contentFilter.categories.streaming": "📺 Streaming",
  "templates.option.CYBERSECURE.contentFilter.categories.games": "🎮 Gamen",
  "templates.field.CYBERSECURE.contentFilter.customBlockedUrls": "Eigen geblokkeerde domeinen",
  "templates.help.CYBERSECURE.contentFilter.customBlockedUrls": "Voeg domeinen één voor één toe",
  "templates.type.COMPANY_DEFAULTS": "Standaarden van het bedrijf",
  "templates.typeDesc.COMPANY_DEFAULTS": "Een bundel sjablonen die op elke nieuwe locatie wordt toegepast",
  "templates.step.COMPANY_DEFAULTS.bundle": "Opgenomen sjablonen",
  "templates.stepDesc.COMPANY_DEFAULTS.bundle": "Op volgorde toegepast wanneer een locatie online komt",
  "templates.field.COMPANY_DEFAULTS.networkTemplateIds": "Netwerksjablonen",
  "templates.field.COMPANY_DEFAULTS.wifiTemplateIds": "Wifisjablonen",
  "templates.field.COMPANY_DEFAULTS.wanTemplateId": "WAN-sjabloon",
  "templates.field.COMPANY_DEFAULTS.cellularTemplateId": "Mobiel sjabloon",
  "templates.field.COMPANY_DEFAULTS.tunnelTemplateId": "Tunnelsjabloon",
  "templates.field.COMPANY_DEFAULTS.systemTemplateId": "Systeemsjabloon",
}

export default templates
